import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface ParishInfo {
  id: string;
  nom?: string;
  name?: string;
  adresse?: string;
  address?: string;
  ville?: string;
  city?: string;
  pays?: string;
  country?: string;
  telephone?: string;
  phone?: string;
  email?: string;
  website?: string;
  /** Heures d'ouverture du secrétariat, texte libre (ParishResponse.openingHours). */
  openingHours?: string;
  saintPatron?: string;
  patronSaint?: string;
  vicariat?: string;
  doyenne?: string;
  /** Libellés du rattachement renvoyés par parish-service (ParishResponse). */
  deaneryName?: string;
  vicariatName?: string;
  description?: string;
  dioceseId?: string;
  qrCode?: string;
  /** Code court de la paroisse, attribué par l'Économat (lecture seule). */
  code?: string;
  // Fiche de la paroisse (questionnaire des paroisses pilotes, 27/10/2026).
  /** Quartier. */
  district?: string;
  /** Repère pour un taxi. */
  landmark?: string;
  /** Boîte postale. */
  postalBox?: string;
  /** Second téléphone (WhatsApp). */
  phone2?: string;
  /** Date de la fête patronale, texte libre. */
  feastDay?: string;
  /** Date d'érection canonique, texte libre. */
  erectionDate?: string;
  /** Nombre approximatif de fidèles le dimanche. */
  parishionerCount?: number | null;
  /** Position GPS lue dans `location` par parish-service. */
  latitude?: number | null;
  longitude?: number | null;
}

export interface MassSchedule {
  id?: string;
  parishId?: string;
  /**
   * Contrat réel de parish-service (MassScheduleService.getScheduleList) :
   * liste plate { day: "Dimanche", time: "09:00", type? }.
   */
  day?: string;
  time?: string;
  jourSemaine?: string;     // ex. "DIMANCHE"
  heure?: string;            // ex. "08:30"
  type?: string;             // "ORDINAIRE" / "SOLENNELLE" / etc.
  langue?: string;
  celebrant?: string;
}

/**
 * parish-service sert les horaires (GET /mass-schedule) sous la forme
 * `{ day: "Dimanche", time: "07:00", type }` : on les ramène au contrat de
 * l'écran (`jourSemaine` en capitales, `heure`). Sans cela, les horaires
 * réels s'affichaient « Autre · » (constaté le 09/10/2026).
 */
export function normaliserHoraire(brut: Record<string, unknown>): MassSchedule {
  const txt = (v: unknown) => (typeof v === "string" && v ? v : undefined);
  const jour = txt(brut.jourSemaine) ?? txt(brut.day) ?? txt(brut.dayOfWeek);
  const heure = txt(brut.heure) ?? txt(brut.time);
  return {
    ...(brut as unknown as MassSchedule),
    jourSemaine: jour ? jour.toUpperCase() : undefined,
    heure: heure ? heure.slice(0, 5) : undefined,
    type: txt(brut.type) ?? txt(brut.massType),
  };
}

/** Horaire tel que le CRUD /masses le sert (avec son identifiant). */
export interface MassItem {
  id: string;
  dayOfWeek: string;
  time: string;
  massType: string;
  intention: string;
  description?: string;
  isActive?: boolean;
  /** Messe célébrée hors de l'église paroissiale (chapelle, communauté...). */
  isSpecialLocation?: boolean | null;
  /** Lieu de la messe quand il n'est pas l'église paroissiale. */
  locationNote?: string | null;
  maxCapacity?: number | null;
  /** Clôture des intentions en ligne, minutes avant la messe (null = règle par défaut). */
  clotureIntentionsMinutes?: number | null;
  /** ORDINAIRE (défaut) ou VACANCES (vacances scolaires), 27/10/2026. */
  periode?: string | null;
}

export function useParishInfo(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["parish-info", parishId],
    enabled: !!parishId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/micro/parishes/${parishId}`);
      return (await res.json()) as ParishInfo;
    },
  });
}

export function useMassSchedule(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["parish-mass-schedule", parishId],
    enabled: !!parishId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/micro/parishes/${parishId}/mass-schedule`);
      const data = await res.json();
      const items = (Array.isArray(data) ? data : data.items ?? []) as Record<string, unknown>[];
      return items.map(normaliserHoraire);
    },
  });
}

/**
 * Coordonnées modifiables depuis l'espace paroisse (upstream
 * ParishPatchRequest, PATCH). Les clés sont celles du service : le français
 * (`telephone`, `adresse`…) était ignoré en silence. Le curé et le secrétaire
 * général n'écrivent que ces champs ; le nom et le rattachement relèvent de
 * l'Économat (parish-service répond 403 sinon).
 */
export interface ParishPatch {
  address?: string;
  city?: string;
  phone?: string;
  email?: string;
  website?: string;
  // Fiche de la paroisse (27/10/2026) : textes libres.
  description?: string;
  district?: string;
  landmark?: string;
  postalBox?: string;
  phone2?: string;
  openingHours?: string;
  patronSaint?: string;
  feastDay?: string;
  erectionDate?: string;
  // Valeurs numériques : parish-service ignore null (pas d'effacement).
  parishionerCount?: number;
  latitude?: number;
  longitude?: number;
}

/** Champs texte de la fiche, tous saisis dans Paramètres › Paroisse. */
export type ChampTexteFiche = Exclude<keyof ParishPatch, "parishionerCount" | "latitude" | "longitude">;

export type Coordonnees = Required<Pick<ParishPatch, ChampTexteFiche>>;

/** Coordonnées et fiche telles que parish-service les sert (clés anglaises ou héritées). */
export function coordonneesEnregistrees(p: ParishInfo | undefined): Coordonnees {
  return {
    address: p?.address ?? p?.adresse ?? "",
    city: p?.city ?? p?.ville ?? "",
    phone: p?.phone ?? p?.telephone ?? "",
    email: p?.email ?? "",
    website: p?.website ?? "",
    description: p?.description ?? "",
    district: p?.district ?? "",
    landmark: p?.landmark ?? "",
    postalBox: p?.postalBox ?? "",
    phone2: p?.phone2 ?? "",
    openingHours: p?.openingHours ?? "",
    patronSaint: p?.patronSaint ?? p?.saintPatron ?? "",
    feastDay: p?.feastDay ?? "",
    erectionDate: p?.erectionDate ?? "",
  };
}

/** Valeurs numériques de la fiche, sous forme de texte pour les champs. */
export interface ChiffresFiche {
  parishionerCount: string;
  latitude: string;
  longitude: string;
}

export function chiffresEnregistres(p: ParishInfo | undefined): ChiffresFiche {
  const txt = (v: number | null | undefined) => (typeof v === "number" && Number.isFinite(v) ? String(v) : "");
  return {
    parishionerCount: txt(p?.parishionerCount),
    latitude: txt(p?.latitude),
    longitude: txt(p?.longitude),
  };
}

/**
 * Corps du PATCH pour les chiffres de la fiche : seuls les chiffres lisibles
 * ET modifiés sont envoyés ; latitude et longitude vont ensemble (parish-service
 * n'écrit la position que s'il reçoit les deux).
 */
export function corpsChiffres(form: ChiffresFiche, enregistre: ChiffresFiche): ParishPatch {
  const body: ParishPatch = {};
  const nombre = (v: string) => {
    const t = v.trim().replace(",", ".");
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };
  const fideles = nombre(form.parishionerCount);
  if (fideles !== null && fideles >= 0 && form.parishionerCount.trim() !== enregistre.parishionerCount.trim()) {
    body.parishionerCount = Math.round(fideles);
  }
  const lat = nombre(form.latitude);
  const lon = nombre(form.longitude);
  const positionChangee = form.latitude.trim() !== enregistre.latitude.trim() || form.longitude.trim() !== enregistre.longitude.trim();
  if (lat !== null && lon !== null && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && positionChangee) {
    body.latitude = lat;
    body.longitude = lon;
  }
  return body;
}

/**
 * Corps du PATCH des coordonnées. Le BFF relaie le corps tel quel et
 * parish-service (ParishWriteService.patch) n'ignore que les champs null :
 * une chaîne vide est écrite, et `@Email` l'accepte. Un champ renseigné est
 * donc envoyé ; un champ VIDÉ alors qu'il portait une valeur est envoyé vide,
 * ce qui l'efface (avant le 10/10/2026 il n'était jamais envoyé : l'adresse,
 * le téléphone ou l'e-mail effacé restait). Un champ vide qui l'était déjà
 * n'est pas envoyé.
 */
export function corpsCoordonnees(form: Coordonnees, enregistre: Coordonnees): ParishPatch {
  const vide = (v: string | undefined) => {
    const t = (v ?? "").trim();
    return t === "" || t === "·";
  };
  const body: ParishPatch = {};
  (Object.keys(form) as ChampTexteFiche[]).forEach((k) => {
    if (!vide(form[k])) body[k] = form[k].trim();
    else if (!vide(enregistre[k])) body[k] = "";
  });
  return body;
}

export function useUpdateParishInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parishId, body }: { parishId: string; body: ParishPatch }) => {
      const res = await apiRequest("PATCH", `/api/micro/parishes/${parishId}`, body);
      return (await res.json()) as ParishInfo;
    },
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: ["parish-info", v.parishId] }),
  });
}

/**
 * Ajout d'un horaire de messe (upstream POST /api/parishes/{id}/masses,
 * MassScheduleDTO). Requis : parishId (@NotNull dans le DTO, ajouté par les
 * deux mutations), dayOfWeek (MONDAY…SUNDAY), time (HH:mm), massType
 * (WEEKDAY/SUNDAY/FEAST/SPECIAL), intention.
 */
export interface CreateMassInput {
  dayOfWeek: string;
  time: string;
  massType: string;
  intention: string;
  description?: string;
  isSpecialLocation?: boolean;
  locationNote?: string | null;
  clotureIntentionsMinutes?: number;
  /** ORDINAIRE ou VACANCES ; absent = ORDINAIRE côté parish-service. */
  periode?: string;
}

export function useCreateMass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parishId, body }: { parishId: string; body: CreateMassInput }) => {
      // Sans parishId, parish-service répond 400 « Constraint Violation »
      // (constaté au tournage du 10/10/2026) : le PUT l'envoyait déjà.
      // Relais BFF POST /masses (27/10/2026), même chemin amont que l'ancien
      // POST /mass-schedule.
      const res = await apiRequest("POST", `/api/micro/parishes/${parishId}/masses`, { ...body, parishId });
      return (await res.json()) as MassSchedule;
    },
    onSuccess: (_, v) => invaliderHoraires(qc, v.parishId),
  });
}

/**
 * Réactivation d'un horaire désactivé (upstream PUT
 * /api/parishes/{id}/masses/{massId}/activate). La désactivation passe par
 * `useDeleteMass` : parish-service ne détruit jamais une messe.
 */
export function useActivateMass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parishId, massId }: { parishId: string; massId: string }) => {
      await apiRequest("PUT", `/api/micro/parishes/${parishId}/masses/${massId}/activate`);
    },
    onSuccess: (_, v) => invaliderHoraires(qc, v.parishId),
  });
}

function invaliderHoraires(qc: ReturnType<typeof useQueryClient>, parishId: string) {
  qc.invalidateQueries({ queryKey: ["parish-mass-schedule", parishId] });
  qc.invalidateQueries({ queryKey: ["parish-masses", parishId] });
}

/**
 * Horaires modifiables (Paramètres › Horaires, manuel 2 Action 04) : liste
 * CRUD de parish-service, avec l'identifiant de chaque messe.
 */
export function useParishMasses(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["parish-masses", parishId],
    enabled: !!parishId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/micro/parishes/${parishId}/masses`);
      const data = await res.json();
      const items = (Array.isArray(data) ? data : data.items ?? []) as MassItem[];
      return items.map((m) => ({ ...m, time: (m.time ?? "").slice(0, 5) }));
    },
  });
}

/** Modification d'un horaire (upstream PUT /api/parishes/{id}/masses/{massId}). */
export function useUpdateMass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parishId, massId, body }: { parishId: string; massId: string; body: CreateMassInput }) => {
      const res = await apiRequest("PUT", `/api/micro/parishes/${parishId}/masses/${massId}`, { ...body, parishId });
      return (await res.json()) as MassItem;
    },
    onSuccess: (_, v) => invaliderHoraires(qc, v.parishId),
  });
}

/** Suppression d'un horaire (upstream DELETE, désactivation côté service). */
export function useDeleteMass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parishId, massId }: { parishId: string; massId: string }) => {
      await apiRequest("DELETE", `/api/micro/parishes/${parishId}/masses/${massId}`);
    },
    onSuccess: (_, v) => invaliderHoraires(qc, v.parishId),
  });
}
